import { useEffect, useState, useRef } from "react";
import { Box, Typography, Link, Button, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/* ======================================================
   GLOBAL TOGGLE
====================================================== */

const ENABLE_TYPING = true; // 🔁 true / false

/* ======================================================
   LABEL CONFIG
====================================================== */

const KEY_LABELS = {
    builder_name: "🧱 Builder",
    project_status: "📌 Project Status",
    project_city: "🌆 City",
    project_location: "📍 Location",
    project_total_land_area: "🌿 Total Land Area",
    project_towers: "🏗️ Towers",
    project_floors: "🧱 Floors",
    project_poss: "📅 Possession Year",
    project_rm_contact_number: "📞 RM Contact"
};

const LINK_KEYS = {
    project_registration_link: "📝 Registration",
    project_broucher_link: "📄 Brochure",
    project_map_location_url: "🗺️ Location Map"
};

const HIDDEN_KEYS = ["_id", "__v", "added_on", "added_by", "project_name"];

/* ======================================================
   UTILS
====================================================== */

const parseIfJson = (text) => {
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
};

const isValidUrl = (v) => typeof v === "string" && v.includes("http");

const extractLink = (value) => {
    if (typeof value !== "string") return null;

    const match = value.match(/https?:\/\/[^\s]+/);
    return match ? match[0] : null;
};

/* ======================================================
   TYPING COMPONENT
====================================================== */

const TypingText = ({ text, speed = 20, onComplete, onType }) => {
    const [displayed, setDisplayed] = useState("");

    useEffect(() => {
        if (!ENABLE_TYPING) {
            setDisplayed(text);
            onComplete && onComplete();
            return;
        }

        let i = 0;
        setDisplayed("");

        const interval = setInterval(() => {
            setDisplayed((prev) => {
                onType && onType();
                return prev + text.charAt(i);
            });
            i++;

            if (i >= text.length) {
                clearInterval(interval);
                onComplete && onComplete();
            }
        }, speed);

        return () => clearInterval(interval);
    }, [text]);

    return (
        <Typography
            fontSize={15}
            color="#ECECF1"
            whiteSpace="pre-wrap"
            lineHeight={1.7}
        >
            {displayed}
        </Typography>
    );
};

/* ======================================================
   FORMAT PROJECT DETAILS
====================================================== */

const buildProjectDetails = (project) => {
    let text = "";

    const toBulletPoints = (val) => {
        if (Array.isArray(val)) return val;
        if (typeof val === "string") {
            return val
                .split(/,|\|/) // handles comma or pipe separated
                .map(v => v.trim())
                .filter(Boolean);
        }
        return [];
    };

    Object.entries(project).forEach(([key, value]) => {
        if (HIDDEN_KEYS.includes(key)) return;

        // ❌ skip empty values
        if (
            value === null ||
            value === undefined ||
            value === "" ||
            (Array.isArray(value) && value.length === 0)
        ) {
            return;
        }

        /* ===============================
           AMENITIES
        =============================== */
        if (key === "project_amenities") {
            text += `\n✨ Amenities:\n`;
            toBulletPoints(value).forEach(item => {
                text += `  • ${item}\n`;
            });
            return;
        }

        /* ===============================
           HIGHLIGHTS
        =============================== */
        if (key === "project_highlights") {
            text += `\n⭐ Highlights:\n`;
            toBulletPoints(value).forEach(item => {
                text += `  • ${item}\n`;
            });
            return;
        }

        /* ===============================
           CONFIGURATIONS
        =============================== */
        if (key === "project_config_array" && Array.isArray(value)) {
            text += `\n🏠 Available Configurations:\n`;
            value.forEach(cfg => {
                if (!cfg.config || !cfg.area || !cfg.budget) return;
                text += `  • ${cfg.config} | ${cfg.area} sq.ft | ₹${cfg.budget} Cr\n`;
            });
            return;
        }

        // Links handled separately
        if (LINK_KEYS[key]) return;

        const label = KEY_LABELS[key] || key.replace(/_/g, " ");

        /* ===============================
           EXTRA SPACE BEFORE RM CONTACT
        =============================== */
        if (key === "project_rm_contact_number") {
            text += `\n`;
        }

        text += `${label}: ${value}\n`;

        /* ===============================
           EXTRA SPACE AFTER RM CONTACT
        =============================== */
        if (key === "project_rm_contact_number") {
            text += `\n`;
        }
    });

    return text.trim();
};




/* ======================================================
   USER BUBBLE
====================================================== */

const UserBubble = ({ text }) => (
    <Box
        alignSelf="flex-end"
        bgcolor="#4F46E5"
        color="white"
        px={2}
        py={1.5}
        borderRadius="12px 12px 0 12px"
        maxWidth="75%"
    >
        <Typography fontSize={15}>{text}</Typography>
    </Box>
);

/* ======================================================
   MAIN COMPONENT
====================================================== */

export default function MessageBubble({ message, chatTypeEffect, isLastMessage, TogglePauseButton, QuickSearch }) {
    const isUser = message.role === "user";
    const parsed = parseIfJson(message.text);

    const [activeIndex, setActiveIndex] = useState(0);
    const [completed, setCompleted] = useState([]);
    const [typingDone, setTypingDone] = useState(false);

    const bottomRef = useRef(null);

    useEffect(() => {
        setActiveIndex(0);
        setCompleted([]);
        setTypingDone(false);
    }, [message.text]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [activeIndex, completed]);

    useEffect(() => {
        if (!chatTypeEffect) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatTypeEffect]);

    const handleShowDetails = (project_name) => {
        QuickSearch("Show details of " + project_name);
        //alert("Show details of" + project_name);
        // 🔥 call API / expand UI / navigate / etc.
    };

    const isEmail = (v) =>
        typeof v === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

    return (
        <Box display="flex" justifyContent="center" py={2} bgcolor="#2F3037">
            <Box width="65%" display="flex" flexDirection="column">
                {isUser ? (
                    <UserBubble text={message.text} />
                )
                    : Array.isArray(parsed) && parsed.length === 0 ?
                        <Box
                            alignSelf="flex-start"
                            bgcolor="#4F46E5"
                            color="white"
                            px={2}
                            py={1.5}
                            borderRadius="12px 12px 0 12px"
                            maxWidth="75%"
                        >
                            <Typography fontSize={15}>{'No Project Found'}</Typography>
                        </Box>
                        : parsed && Array.isArray(parsed) ? (
                            (chatTypeEffect
                                ? parsed.slice(0, activeIndex + 1)
                                : parsed
                            ).map((project, index) => (
                                <Box key={index} mb={4}>
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        color="#ECECF1"
                                        mb={1}
                                    >
                                        🏢 {project.project_name}

                                        {/* 👇 Show button ONLY if builder_name is missing */}
                                        {!project.builder_name && (
                                            <IconButton
                                                size="small"
                                                onClick={() => handleShowDetails(project.project_name)}
                                                sx={{
                                                    color: "#4EA1FF",
                                                    "&:hover": {
                                                        backgroundColor: "rgba(78,161,255,0.12)",
                                                    },
                                                }}
                                            >
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Typography>



                                    {(chatTypeEffect && isLastMessage) && index === activeIndex && !typingDone ? (
                                        <TypingText
                                            text={buildProjectDetails(project)}
                                            onType={() =>
                                                bottomRef.current?.scrollIntoView({ behavior: "smooth" })
                                            }
                                            onComplete={() => {
                                                setCompleted((p) => [...p, index]);
                                                TogglePauseButton();
                                                index === parsed.length - 1
                                                    ? setTypingDone(true)
                                                    : setActiveIndex(index + 1);
                                            }}
                                        />
                                    ) : (
                                        <Typography
                                            fontSize={15}
                                            color="#ECECF1"
                                            whiteSpace="pre-wrap"
                                            lineHeight={1.7}
                                        >
                                            {buildProjectDetails(project)}
                                        </Typography>
                                    )}

                                    {(!chatTypeEffect || completed.includes(index)) &&
                                        Object.entries(LINK_KEYS).map(([key, label]) => {
                                            if (!project[key]) return null;

                                            let value = project[key];
                                            // value = value.replace('By link', '').replace('By mail', '')

                                            // ✅ URL
                                            if (isValidUrl(value)) {
                                                return (
                                                    <Typography key={key} mt={0.6}>
                                                        <Link href={extractLink(value)} target="_blank" color="#4EA1FF">
                                                            {label}
                                                        </Link>
                                                    </Typography>
                                                );
                                            }

                                            // ✅ Email (Registration only)
                                            if (key === "project_registration_link" && isEmail(value)) {
                                                return (
                                                    <Typography key={key} mt={0.6}>
                                                        <Link href={`mailto:${value}`} color="#4EA1FF">
                                                            {label}
                                                        </Link>
                                                    </Typography>
                                                );
                                            }

                                            // ✅ Plain text (Registration only)
                                            if (key === "project_registration_link") {
                                                return (
                                                    <Typography
                                                        key={key}
                                                        mt={0.6}
                                                        fontSize={14}
                                                        color="#ECECF1"
                                                    >
                                                        {label}: {value}
                                                    </Typography>
                                                );
                                            }

                                            return null;
                                        })}

                                </Box>
                            ))
                        ) : (
                            <Typography color="#ECECF1">{message.text}</Typography>
                        )}

                <div ref={bottomRef} />
            </Box>
        </Box>
    );
}
